import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { isStringEmpty, stringEmptyDefault } from '@common/helpers';
import { LABEL_LOUNGE_NAME, LOUNGE_SSR_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { ChangeLoungeData, LoungeDisplayData } from '@common/interfaces/mybooking/lounge-service-request.interface';
import {
  ChargeableLounge,
  ChargeableLoungePassengerApplyStatusMap,
} from '@common/interfaces/mybooking/mybooking.interface';
import { Lounge, PassengerLoungeMap } from '@common/interfaces/mybooking/order-services.interface';
import { ServicingCommonStoreService } from '@common/services';
import { LoungeServiceRequestService } from '@common/services/my-booking/lounge-service-request/lounge-service-request.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { SupportComponent } from '@lib/components/support-class';
import { AmountFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { GetOrderResponseData, GetOrderResponseDataOrderType } from 'src/sdk-servicing';
import { OrderSeatItemSeatSelection } from 'src/sdk-servicing/model/orderSeatItemSeatSelection';
import { v4 as uuidV4 } from 'uuid';

@Component({
  selector: 'asw-lounge-cataloge',
  templateUrl: './lounge-cataloge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoungeCatalogeComponent extends SupportComponent {
  pnr!: GetOrderResponseData;
  @Input() flightId?: string = '';
  @Input() travelerId?: string = '';

  @Output() changeLounge = new EventEmitter<ChangeLoungeData>();

  passengerLoungeInfo: PassengerLoungeMap = {};

  loungeDisplay: LoungeDisplayData[] = [];

  isExistCode: boolean = false;

  _subscription: Subscription = new Subscription();

  uuid: string = uuidV4();

  chargeableLounge!: ChargeableLounge;
  constructor(
    private _common: CommonLibService,
    public _amountFormatPipe: AmountFormatPipe,
    private _myBookingServiceService: MyBookingServiceService,
    private _loungeServiceRequestService: LoungeServiceRequestService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _changeDetector: ChangeDetectorRef,
    private staticMsg: StaticMsgPipe
  ) {
    super(_common);
  }

  init(): void {
    this._subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this.chargeableLounge = servicingCommon.chargeableLounge ?? ({} as ChargeableLounge);
        this.mappingInItLoungeCatalog();
      })
    );
  }

  mappingInItLoungeCatalog() {
    this.passengerLoungeInfo = this._loungeServiceRequestService.getLoungeService(
      String(this.flightId),
      String(this.travelerId),
      this.pnr?.services?.lounge as Lounge
    );
    // [13.1] 当該搭乗者ラウンジ情報.codeに値が存在しない場合、
    this.passengerLoungeInfo?.catalogue?.forEach((catalogue: any) => {
      const loungeCode = stringEmptyDefault(this.passengerLoungeInfo?.code);
      //  1.当該搭乗者ラウンジ情報.codeに値が存在しない場合、
      if (isStringEmpty(loungeCode)) {
        const label = this.getApplicationStatus(Number(catalogue?.quota), loungeCode);
        // [13.1.1]
        const loungeItem =
          (this.chargeableLounge?.[this.flightId!] as ChargeableLoungePassengerApplyStatusMap)?.[this.travelerId!] ??
          {};
        //  1.1.更新サービス情報.セグメントマップ<当該セグメント情報.id>.搭乗者別申込状況マップ<当該搭乗者情報.id>.SSRコード = 当該カタログ情報.codeが存在する場合、選択済み活性で表示
        if (loungeItem?.ssrCode === catalogue?.code) {
          this.loungeDisplay.push({
            ssrCode: catalogue?.code,
            paidServiceAmount: catalogue?.prices?.total,
            currencyCode: catalogue?.prices?.currencyCode,
            isActive: true,
            isSelected: true,
          });
        } else if (!isStringEmpty(label)) {
          // [13.1.2] 後述の「申込状態」ラベルを表示する場合、非表示
          this.loungeDisplay.push({
            ssrCode: catalogue?.code,
            isDisplayStatus: true,
            status: label,
          });
        } else {
          // [13.1.3].上記以外の場合、活性で未選択表示する。
          this.loungeDisplay.push({
            ssrCode: catalogue?.code,
            paidServiceAmount: catalogue?.prices?.total,
            currencyCode: catalogue?.prices?.currencyCode,
            isActive: true,
            isSelected: false,
          });
        }
      }
      // [13.2] 当該搭乗者ラウンジ情報.codeに値が存在する場合、.申込状態が以下いずれかに該当する場合、非活性で選択済み
      else if (
        !isStringEmpty(this.passengerLoungeInfo?.code) &&
        // [13.2.1] 当該搭乗者ラウンジ情報.serviceStatus="confirm"
        (this.passengerLoungeInfo?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed ||
          // [13.2.2] 当該搭乗者ラウンジ情報.serviceStatus="confirmedWithoutTsm"かつ、PNR情報取得API応答.data.orderType.channelType="nh"
          (this.passengerLoungeInfo?.serviceStatus ===
            OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm &&
            this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh))
      ) {
        this.loungeDisplay.push({
          ssrCode: this.passengerLoungeInfo?.code,
          paidServiceAmount: this.passengerLoungeInfo?.prices?.total,
          currencyCode: this.passengerLoungeInfo?.prices?.currencyCode,
          isActive: false,
          isSelected: true,
        });
      }
    });
    this._changeDetector.detectChanges();
  }

  // [11] ラウンジ種別
  getLoungeType(catalog: LoungeDisplayData) {
    const loungeServiceOfTraveler = this._loungeServiceRequestService.getLoungeService(
      this.flightId,
      this.travelerId,
      this.pnr?.services?.lounge as Lounge
    );
    // 1.当該搭乗者ラウンジ情報.codeが存在する場合

    if (loungeServiceOfTraveler?.code) {
      return this.getLoungeLabel(catalog?.ssrCode ?? '');
    } else {
      // 2.上記以外の label.loungeName.ssrLoug1
      // ・カタログコードがLOUGの場合、label.loungeName.ssrLoug1
      if (catalog?.ssrCode === LOUNGE_SSR_CODE.LOUG) {
        return LABEL_LOUNGE_NAME.LOUG1;
      }

      // ・カタログコードがMYLGの場合、label.loungeName.ssrMylg
      if (catalog?.ssrCode === LOUNGE_SSR_CODE.MYLG) {
        return LABEL_LOUNGE_NAME.MYLG;
      }
      return '';
    }
  }

  getLoungeLabel(code: string) {
    if (code === LOUNGE_SSR_CODE.LOUG) {
      // ・SSRコードがLOUGの場合、 label.loungeName.ssrLoug2
      return LABEL_LOUNGE_NAME.LOUG2;
    } else if (code === LOUNGE_SSR_CODE.MYLG) {
      // SSRコードがMYLGの場合、label.loungeName.ssrMylg
      return LABEL_LOUNGE_NAME.MYLG;
    }
    return '';
  }

  // [14] 申込状態
  getApplicationStatus(quota: number, longeCode: string) {
    // [14.1] 当該搭乗者ラウンジ情報.codeに値が存在しない場合、以下の文言を設定する。
    if (isStringEmpty(longeCode)) {
      if (Number(quota) === 0) {
        return this.staticMsg.transform('label.unavailable.noStock');
      } else if (this.passengerLoungeInfo?.isWaived) {
        return this.staticMsg.transform('label.useFree');
      } else {
        return '';
      }
    } else {
      // [14.2] 当該搭乗者ラウンジ情報.codeに値が存在する場合、当該搭乗者ラウンジ情報.serviceStatusに応じて以下内容を表示
      switch (this.passengerLoungeInfo?.serviceStatus) {
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Prebooked:
          return this.staticMsg.transform('label.waitingForEmd');
        case OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm:
          if (this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds) {
            return this.staticMsg.transform('label.appliedByOtherCarrier');
          } else {
            return '';
          }
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Requesting:
          return this.staticMsg.transform('label.applying');
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled:
          return this.staticMsg.transform('label.notApplied');
        default:
          return '';
      }
    }
  }

  // (4) ラウンジ選択値変更処理
  onCheckboxChange(e: Event, catalogItem?: LoungeDisplayData) {
    const checked = (e.target as HTMLInputElement).checked;
    // (4.1) チェックありにした場合、当該ラウンジ申込情報内の他のラウンジ選択を未選択に切り替える。
    let paramUpdate = {
      isCheck: checked,
      flightId: String(this.flightId),
      travelerId: String(this.travelerId),
    };
    if (checked) {
      this.loungeDisplay = this.loungeDisplay.map((cataloge: LoungeDisplayData) => {
        if (cataloge.ssrCode === catalogItem?.ssrCode) {
          return {
            ...cataloge,
            isSelected: true,
          };
        } else {
          return {
            ...cataloge,
            isSelected: false,
          };
        }
      });
      paramUpdate = {
        ...paramUpdate,
        ...catalogItem,
      };
      this.changeLounge.emit(paramUpdate);
    } else {
      this.changeLounge.emit(paramUpdate);
    }
  }

  destroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  reload(): void {}
}
